// Tracks which notification ids have already been shown as a floating toast
// (NotificationTray), independent of the notification's own unread/dismissed
// status in the backend. Without this, the tray would re-show the same
// still-unread notification every time it remounts — which happens on every
// navigation between Chat and Dashboard/Settings (each page mounts its own
// NotificationsProvider + Tray instance, see NotificationsContext.tsx) and on
// every fresh login/reload. A toast is a one-time "you've been shown this"
// event; the bell's persistent list is the real record of what's unread.
//
// Backed by localStorage (not just in-memory) so it survives a reload/relogin
// too, not only in-session navigation — matches the user's report that it was
// popping again "every time I switch screen or log back in".
const STORAGE_KEY = 'zeph:toastedNotificationIds'
// Caps unbounded growth for a long-lived browser profile — this is a "have I
// shown this" marker, not an audit trail, so pruning the oldest entries once
// the set gets large is safe.
const MAX_TRACKED = 500

function readIds(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? new Set(parsed) : new Set()
  } catch {
    return new Set()
  }
}

function writeIds(ids: Set<string>): void {
  try {
    const list = [...ids]
    const trimmed = list.length > MAX_TRACKED ? list.slice(list.length - MAX_TRACKED) : list
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
  } catch {
    // Storage blocked/full (private mode, quota) — worst case a notification
    // toasts again next time, which is the pre-existing behavior anyway.
  }
}

export function hasBeenToasted(id: string): boolean {
  return readIds().has(id)
}

export function markToasted(id: string): void {
  const ids = readIds()
  if (ids.has(id)) return
  ids.add(id)
  writeIds(ids)
}
